/**
 * Outsource Partner Portal — Case Detail (Phase 2).
 * Shows the "Assigned bankers (multi-fanout)" table and lets the
 * outsource user add/update/delete banker sub-rows sourced from their
 * pre-registered roster.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Handshake, LogOut, ArrowLeft, Plus, Trash2, Loader2, Activity } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'waiting_review', label: 'Under review', tone: 'bg-amber-500 text-white' },
  { value: 'approved', label: 'Approved', tone: 'bg-emerald-500 text-white' },
  // Legacy statuses kept only so historical rows still render a badge.
  { value: 'under_review', label: 'Under review', tone: 'bg-amber-500 text-white' },
  { value: 'kiv', label: 'KIV', tone: 'bg-amber-500 text-white' },
  { value: 'rejected', label: 'Rejected', tone: 'bg-red-500 text-white' },
  { value: 'withdrawn', label: 'Withdrawn', tone: 'bg-gray-400 text-white' },
];

function statusMeta(value) {
  return STATUS_OPTIONS.find(o => o.value === value) || STATUS_OPTIONS[0];
}

function ago(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins || 1}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 0 ? 'Today' : `${days}d ago`;
}

export default function OutsourceCaseDetail() {
  const navigate = useNavigate();
  const { case_id } = useParams();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const [busy, setBusy] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addBankerId, setAddBankerId] = useState('');
  const [addNotes, setAddNotes] = useState('');

  const token = () => localStorage.getItem('outsource_token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [detail, audit] = await Promise.all([
        axios.get(`${API_BASE}/api/outsource/cases/${case_id}`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/api/outsource/cases/${case_id}/audit?limit=50`, { headers: authHeaders() }),
      ]);
      setData(detail.data);
      setFeed(audit.data.entries || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('outsource_token');
        navigate('/outsource/login');
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to load case');
    } finally { setBusy(false); }
  }, [case_id, navigate]);

  useEffect(() => {
    if (!token()) { navigate('/outsource/login'); return; }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem('outsource_token');
    localStorage.removeItem('outsource_profile');
    navigate('/outsource/login');
  };

  const submitAdd = async () => {
    if (!addBankerId) { toast.error('Pick a banker from your roster'); return; }
    try {
      await axios.post(
        `${API_BASE}/api/outsource/cases/${case_id}/assignments`,
        { outsource_banker_id: addBankerId, notes: addNotes || null },
        { headers: authHeaders() },
      );
      toast.success('Banker fan-out added — case is now in their inbox');
      setAddOpen(false); setAddBankerId(''); setAddNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.bank} · ${row.officer_name}?`)) return;
    try {
      await axios.delete(
        `${API_BASE}/api/outsource/cases/${case_id}/assignments/${row.id}`,
        { headers: authHeaders() },
      );
      toast.success('Row removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  if (busy || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const c = data.case || {};
  const assignments = data.assignments || [];
  const roster = data.roster || [];
  const rosterUsedIds = new Set(assignments.map(a => a.outsource_banker_id));
  const rosterAvailable = roster.filter(r => !rosterUsedIds.has(r.id));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="outsource-case-detail">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Handshake className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Outsource Partner Portal</h1>
              <p className="text-xs text-gray-500">Signed in as {data.partner?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/outsource/dashboard')} data-testid="outsource-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="outsource-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>

        {/* Case header */}
        <div className="bg-white rounded-2xl border p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900" data-testid="outsource-case-header">
                Case {c.case_ref || c.id?.slice(0, 8)} · <span className="text-gray-700">{c.customer_name || '—'}</span>
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {c.property_name || '—'}
                {c.unit_ref ? ` · #${c.unit_ref}` : ''}
                {c.loan_amount_requested ? ` · RM ${Number(c.loan_amount_requested).toLocaleString('en-MY')}` : ''}
                {c.created_at ? ` · received ${new Date(c.created_at).toLocaleDateString('en-MY', { day: '2-digit', month: 'short' })}` : ''}
              </p>
            </div>
            <Badge className="bg-indigo-600 text-white text-sm px-3 py-1" data-testid="outsource-assigned-count">
              {assignments.length === 0 ? 'No bankers yet' : `Assigned to ${assignments.length} banker${assignments.length > 1 ? 's' : ''}`}
            </Badge>
          </div>
        </div>

        {/* Multi-fanout table */}
        <div className="bg-white rounded-2xl border p-5">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-800">Assigned bankers (multi-fanout)</h3>
            <p className="text-xs text-gray-500 mt-1">
              Assign this case to 1 or many bankers in parallel. Multiple bankers can submit and update their offers independently — the customer decides which one to accept.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500">
                <tr className="border-b">
                  <th className="text-left p-2">Bank</th>
                  <th className="text-left p-2">Branch</th>
                  <th className="text-left p-2">Team Leader</th>
                  <th className="text-left p-2">Officer</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Updated</th>
                  <th className="text-right p-2">Approval</th>
                  <th className="text-right p-2">Fee on approval</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map(row => {
                  const s = statusMeta(row.status);
                  return (
                    <tr key={row.id} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`outsource-assignment-row-${row.id}`}>
                      <td className="p-2 font-medium">{row.bank || '—'}</td>
                      <td className="p-2 text-xs">{row.branch || '—'}</td>
                      <td className="p-2 text-xs text-indigo-700">{row.team_leader || '—'}</td>
                      <td className="p-2">{row.officer_name || '—'}</td>
                      <td className="p-2">
                        <span
                          className={`px-3 py-1 rounded-md text-xs font-semibold ${s.tone} inline-block`}
                          data-testid={`outsource-status-${row.id}`}
                        >
                          {s.label}
                        </span>
                      </td>
                      <td className="p-2 text-xs text-gray-500">{ago(row.updated_at)}</td>
                      <td className="p-2 text-right text-xs">
                        {row.approval_amount
                          ? `RM ${Number(row.approval_amount).toLocaleString('en-MY')}${row.approval_rate_percent ? ` @ ${row.approval_rate_percent}%` : ''}`
                          : '—'}
                      </td>
                      <td className="p-2 text-right text-xs text-indigo-700 font-semibold">
                        {row.fee_on_approval ? `RM ${Number(row.fee_on_approval).toLocaleString('en-MY')}` : '—'}
                      </td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeRow(row)} className="text-red-600" data-testid={`outsource-remove-${row.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {assignments.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-center text-gray-400 text-xs">
                    No bankers assigned yet. Click <em>Assign another banker</em> below to fan-out.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pt-4">
            <Button
              variant="outline"
              className="border-indigo-300 text-indigo-700"
              onClick={() => setAddOpen(true)}
              disabled={rosterAvailable.length === 0 && rosterUsedIds.size >= roster.length}
              data-testid="outsource-add-banker-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Assign another banker
            </Button>
            {roster.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                You have no bankers on your roster yet. Ask your SAM Rumah admin to add bankers under your partner profile before assigning.
              </p>
            )}
          </div>
        </div>

        {/* Activity feed */}
        <div className="bg-white rounded-2xl border p-5" data-testid="outsource-activity-feed">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <Activity className="w-4 h-4" />
            </div>
            <h3 className="font-semibold text-gray-800">Activity feed</h3>
            <span className="text-xs text-gray-400 ml-auto">{feed.length} event{feed.length === 1 ? '' : 's'}</span>
          </div>
          {feed.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-4">No activity yet.</p>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
              {feed.map(e => (
                <li key={e.id} className="pl-4 relative" data-testid={`outsource-feed-${e.id}`}>
                  <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${
                    e.action === 'outsource_assigned' ? 'bg-indigo-500' :
                    e.action === 'outsource_assignment_added' ? 'bg-blue-500' :
                    e.action === 'outsource_assignment_removed' ? 'bg-red-500' :
                    e.after_status === 'approved' ? 'bg-emerald-500' :
                    e.after_status === 'rejected' ? 'bg-red-500' :
                    e.after_status === 'kiv' ? 'bg-amber-500' : 'bg-slate-400'
                  }`} />
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">
                      {e.action === 'outsource_assigned' && 'Case assigned to this agency'}
                      {e.action === 'outsource_assignment_added' && 'Banker fan-out added'}
                      {e.action === 'outsource_assignment_updated' && 'Banker fan-out updated'}
                      {e.action === 'outsource_assignment_removed' && 'Banker fan-out removed'}
                    </span>
                    <span className="text-xs text-gray-500">{new Date(e.changed_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {e.notes && <p className="text-xs text-gray-600 mt-1">{e.notes}</p>}
                  <p className="text-[10px] text-gray-400 mt-1">
                    by {e.changed_by_name || e.changed_by || '—'}{e.changed_by_role ? ` · ${e.changed_by_role}` : ''}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign banker to this case</DialogTitle>
            <DialogDescription className="text-xs">
              Bankers come from your pre-registered roster. Ask your admin to update the roster if the officer you need is missing.
              <br />
              <span className="text-emerald-700 font-medium">The case will immediately appear in that banker&apos;s portal as &quot;Under review&quot; — they submit the Approval Amount + Rate from there.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Banker</Label>
              <Select value={addBankerId} onValueChange={setAddBankerId}>
                <SelectTrigger data-testid="outsource-add-banker-select"><SelectValue placeholder="Pick a banker" /></SelectTrigger>
                <SelectContent>
                  {rosterAvailable.length === 0 && <SelectItem value="__none__" disabled>All roster bankers already assigned</SelectItem>}
                  {rosterAvailable.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.bank || '—'} · {b.name || 'Unnamed'}
                      {b.branch ? ` (${b.branch})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={addNotes} onChange={e => setAddNotes(e.target.value)} data-testid="outsource-add-notes" />
            </div>
            <Button onClick={submitAdd} className="w-full" data-testid="outsource-add-submit">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
